import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../context/AuthContext';
import type { Notice, Tenant, ImportJob, TenantDomain } from '../../types/admin';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Download, FileText, ClipboardPaste, Upload, AlertCircle } from 'lucide-react';
import {
  SAMPLE_CSV,
  formatFileSize,
  parseApiErrorPayload,
  type ComposerSubmissionMode,
  type CsvJobCreateResponse,
  type ImportSourceMode,
  type PasteFormat,
  type ValidationPayloadResponse,
  type ValidationPreview,
} from './import-utils';

interface ImportComposerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTenant: Tenant;
  selectedTenantId: string | null;
  domains: TenantDomain[];
  canWriteLinks: boolean;
  token: string | null;
  setNotice: (notice: Notice) => void;
  onCreated: (job: ImportJob) => Promise<void> | void;
}

export default function ImportComposerDialog({
  open,
  onOpenChange,
  selectedTenant,
  selectedTenantId,
  domains,
  canWriteLinks,
  token,
  setNotice,
  onCreated,
}: ImportComposerDialogProps) {
  const { t } = useTranslation();
  const [sourceMode, setSourceMode] = useState<ImportSourceMode>('csv');
  const [submissionMode, setSubmissionMode] = useState<ComposerSubmissionMode>('review');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [pasteFormat, setPasteFormat] = useState<PasteFormat>('csv');
  const [pasteInput, setPasteInput] = useState(SAMPLE_CSV);
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [composerPreview, setComposerPreview] = useState<ValidationPreview | null>(null);
  const [validatingComposer, setValidatingComposer] = useState(false);
  const [creatingImport, setCreatingImport] = useState(false);

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const composerUsesAsyncValidation = sourceMode === 'csv';
  const composerSubmissionLabel = composerUsesAsyncValidation
    ? (submissionMode === 'start' ? t('imports.composer.submitUploadStart') : t('imports.composer.submitUploadValidate'))
    : submissionMode === 'start'
      ? t('imports.composer.submitCreateStart')
      : t('imports.composer.submitCreateReview');

  useEffect(() => {
    setComposerPreview(null);
  }, [csvFile, pasteFormat, pasteInput, sourceMode]);

  const activeDomains = useMemo(
    () => domains.filter((domain) => domain.status === 'active'),
    [domains],
  );

  useEffect(() => {
    if (activeDomains.length === 0) {
      setSelectedDomainId('');
      return;
    }

    setSelectedDomainId((current) => {
      if (current && activeDomains.some((domain) => domain.domainId === current)) {
        return current;
      }

      return activeDomains.find((domain) => domain.isDefault)?.domainId || activeDomains[0].domainId;
    });
  }, [activeDomains, open]);

  const resetState = () => {
    setSourceMode('csv');
    setSubmissionMode('review');
    setCsvFile(null);
    setPasteFormat('csv');
    setPasteInput(SAMPLE_CSV);
    setComposerPreview(null);
  };

  const executeJobAction = async (jobId: string, action: 'validate' | 'start'): Promise<ImportJob> => {
    const response = await fetch(`${API_BASE}/import-jobs/${encodeURIComponent(jobId)}/${action}`, {
      method: 'POST',
      headers: authHeaders,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw parseApiErrorPayload(data, `Failed to ${action} import job`);
    }

    const data = await response.json();
    return data.job as ImportJob;
  };

  const createCsvImportJob = async (): Promise<ImportJob> => {
    if (!selectedTenantId) {
      throw new Error(t('imports.composer.errorNoWorkspace'));
    }

    if (!csvFile) {
      throw new Error(t('imports.composer.errorNoFile'));
    }

    const createResponse = await fetch(`${API_BASE}/import-jobs/csv`, {
      method: 'POST',
      headers: authHeaders,
        body: JSON.stringify({
          tenantId: selectedTenantId,
          fileName: csvFile.name,
          domainId: selectedDomainId || undefined,
        }),
      });

    if (!createResponse.ok) {
      const data = await createResponse.json().catch(() => null);
      throw parseApiErrorPayload(data, 'Failed to create CSV import job');
    }

    const createData = await createResponse.json() as CsvJobCreateResponse;
    let uploadResponse: Response;

    try {
      uploadResponse = await fetch(createData.uploadUrl, {
        method: createData.uploadMethod,
        headers: {
          'Content-Type': createData.uploadContentType,
        },
        body: csvFile,
      });
    } catch (networkError) {
      throw new Error(
        t('imports.composer.errorUploadFailed', {
          message: networkError instanceof Error ? networkError.message : String(networkError),
        }),
      );
    }

    if (!uploadResponse.ok) {
      const errorBody = await uploadResponse.text().catch(() => '');
      const s3Message = errorBody.match(/<Message>(.*?)<\/Message>/)?.[1] || '';
      throw new Error(
        t('imports.composer.errorUploadHttp', {
          status: uploadResponse.status,
          message: s3Message || t('imports.composer.errorS3Config'),
        }),
      );
    }

    return createData.job;
  };

  const validateComposerSource = async (): Promise<ValidationPreview | null> => {
    if (!selectedTenantId) {
      setNotice({ type: 'error', message: t('imports.composer.errorNoWorkspace') });
      return null;
    }

    if (sourceMode === 'csv') {
      return null;
    }

    try {
      setValidatingComposer(true);

      let payload: Record<string, unknown>;
      if (pasteFormat === 'json') {
        const parsed = JSON.parse(pasteInput);
        if (!Array.isArray(parsed)) {
          throw new Error('JSON must be an array');
        }

        payload = {
          tenantId: selectedTenantId,
          links: parsed,
        };
      } else {
        payload = {
          tenantId: selectedTenantId,
          csvText: pasteInput,
        };
      }

      const response = await fetch(`${API_BASE}/import-jobs/validate`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw parseApiErrorPayload(data, t('imports.composer.errorValidation'));
      }

      const data = await response.json() as ValidationPayloadResponse;
      const preview: ValidationPreview = {
        summary: data.validation,
        normalizedLinks: Array.isArray(data.normalizedLinks) ? data.normalizedLinks : [],
        canStart: Boolean(data.canStart ?? (data.validation.invalidRows === 0 && data.validation.validRows > 0)),
      };
      setComposerPreview(preview);

      if (preview.summary.invalidRows > 0) {
        setNotice({ type: 'error', message: t('imports.composer.noticeValidationIssues', { n: preview.summary.invalidRows }) });
      } else {
        setNotice({ type: 'success', message: t('imports.composer.noticeValidationOk', { n: preview.summary.validRows }) });
      }

      return preview;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('imports.composer.errorValidation');
      setNotice({ type: 'error', message });
      setComposerPreview(null);
      return null;
    } finally {
      setValidatingComposer(false);
    }
  };

  const createImportFromComposer = async () => {
    if (!selectedTenantId) {
      setNotice({ type: 'error', message: t('imports.composer.errorNoWorkspace') });
      return;
    }

    if (!canWriteLinks) {
      setNotice({ type: 'error', message: t('imports.composer.errorNoPermission') });
      return;
    }

    try {
      setCreatingImport(true);

      let createdJob: ImportJob;
      let createdValidRows = 0;

      if (sourceMode === 'csv') {
        createdJob = await createCsvImportJob();
        createdJob = submissionMode === 'start'
          ? await executeJobAction(createdJob.jobId, 'start')
          : await executeJobAction(createdJob.jobId, 'validate');
      } else {
        const preview = composerPreview || await validateComposerSource();
        if (!preview) {
          return;
        }

        if (preview.summary.invalidRows > 0 || preview.summary.validRows === 0) {
          setNotice({ type: 'error', message: t('imports.composer.errorInvalidData') });
          return;
        }

        const response = await fetch(`${API_BASE}/import-jobs/api`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            tenantId: selectedTenantId,
            links: preview.normalizedLinks,
            domainId: selectedDomainId || undefined,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw parseApiErrorPayload(data, t('imports.composer.errorCreate'));
        }

        const data = await response.json();
        createdJob = data.job as ImportJob;
        if (submissionMode === 'start') {
          createdJob = await executeJobAction(createdJob.jobId, 'start');
        }
        createdValidRows = preview.summary.validRows;
      }

      resetState();
      onOpenChange(false);
      await onCreated(createdJob);
      setNotice({
        type: 'success',
        message: sourceMode === 'csv'
          ? (submissionMode === 'start' ? t('imports.composer.successCsvStart') : t('imports.composer.successCsvReview'))
          : (submissionMode === 'start'
            ? t('imports.composer.successApiStart', { n: createdValidRows })
            : t('imports.composer.successApiReview', { n: createdValidRows })),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('imports.composer.errorCreate');
      setNotice({ type: 'error', message });
    } finally {
      setCreatingImport(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('imports.composer.title')}</DialogTitle>
          <DialogDescription>{t('imports.composer.desc', { tenantName: selectedTenant.tenantName })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('imports.composer.domainLabel')}</label>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={selectedDomainId} onChange={(event) => setSelectedDomainId(event.target.value)} disabled={activeDomains.length === 0}>
              {activeDomains.length === 0 ? (
                <option value="">{t('imports.composer.domainFallback')}</option>
              ) : (
                activeDomains.map((domain) => (
                  <option key={domain.domainId} value={domain.domainId}>{domain.domain}</option>
                ))
              )}
            </select>
            <p className="text-xs text-muted-foreground">
              {activeDomains.length === 0 ? t('imports.composer.domainFallbackHint') : t('imports.composer.domainHint')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                'import-source-card rounded-lg border p-4 text-left transition-colors hover:bg-accent/50',
                sourceMode === 'csv' && 'is-active ring-2 ring-primary',
              )}
              onClick={() => setSourceMode('csv')}
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Upload className="h-4 w-4" />
                {t('imports.composer.sourceCsv')}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t('imports.composer.sourceCsvSub')}</p>
            </button>

            <button
              type="button"
              className={cn(
                'import-source-card rounded-lg border p-4 text-left transition-colors hover:bg-accent/50',
                sourceMode === 'paste' && 'is-active ring-2 ring-primary',
              )}
              onClick={() => setSourceMode('paste')}
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ClipboardPaste className="h-4 w-4" />
                {t('imports.composer.sourcePaste')}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t('imports.composer.sourcePasteSub')}</p>
            </button>
          </div>

          {sourceMode === 'csv' ? (
            <div className="space-y-3 rounded-lg border border-dashed p-4">
              <input
                type="file"
                aria-label="CSV file input"
                accept=".csv,text/csv"
                className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                onChange={(event) => setCsvFile(event.target.files?.[0] || null)}
              />

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                {csvFile
                  ? t('imports.composer.fileInfo', { name: csvFile.name, size: formatFileSize(csvFile.size) })
                  : t('imports.composer.noFile')}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
                  const blobUrl = URL.createObjectURL(blob);
                  const anchor = document.createElement('a');
                  anchor.href = blobUrl;
                  anchor.download = 'shortlink-import-sample.csv';
                  document.body.appendChild(anchor);
                  anchor.click();
                  anchor.remove();
                  URL.revokeObjectURL(blobUrl);
                }}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {t('imports.composer.sampleCsv')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2" role="tablist" aria-label="Paste format">
                <Button
                  aria-label="Formato CSV"
                  variant={pasteFormat === 'csv' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPasteFormat('csv')}
                >
                  CSV
                </Button>
                <Button
                  aria-label="Formato JSON"
                  variant={pasteFormat === 'json' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPasteFormat('json')}
                >
                  JSON
                </Button>
              </div>

              <Textarea
                aria-label="Bulk import payload"
                value={pasteInput}
                onChange={(event) => setPasteInput(event.target.value)}
                rows={12}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder={pasteFormat === 'json'
                  ? '[{"originalUrl":"https://example.com","redirectType":"301"}]'
                  : 'urlcode,url,redirectType\n,https://example.com,301'}
              />

              {composerPreview && (
                <div className="import-composer-section rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{t('imports.composer.validationResult')}</span>
                    <Badge
                      className={cn('composer-state-chip', composerPreview.summary.invalidRows === 0 && 'is-ready')}
                      variant={composerPreview.summary.invalidRows > 0 ? 'destructive' : 'success'}
                    >
                      {t('imports.composer.validationBadge', {
                        valid: composerPreview.summary.validRows,
                        invalid: composerPreview.summary.invalidRows,
                      })}
                    </Badge>
                  </div>

                  {composerPreview.summary.issues.length > 0 && (
                    <div className="error-list mt-3 flex flex-col gap-2">
                      {composerPreview.summary.issues.slice(0, 4).map((issue) => (
                        <div key={`${issue.row}-${issue.message}`} className="flex items-start gap-2 rounded-md bg-destructive/5 p-2 text-xs text-destructive">
                          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>row {issue.row}: {issue.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-col">
          <div className="flex w-full items-center gap-2">
            <Button
              className={cn('import-mode-chip', submissionMode === 'review' && 'is-active')}
              variant={submissionMode === 'review' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSubmissionMode('review')}
            >
              {composerUsesAsyncValidation ? t('imports.composer.modeUploadValidate') : t('imports.composer.modeCreateReview')}
            </Button>
            <Button
              className={cn('import-mode-chip', submissionMode === 'start' && 'is-active')}
              variant={submissionMode === 'start' ? 'default' : 'outline'}
              size="sm"
              aria-label="Start Immediately"
              onClick={() => setSubmissionMode('start')}
            >
              {composerUsesAsyncValidation ? t('imports.composer.modeUploadStart') : t('imports.composer.modeStartImmediately')}
            </Button>
          </div>

          <div className="modal-actions flex w-full items-center justify-end gap-2">
            {!composerUsesAsyncValidation && (
              <Button
                variant="outline"
                onClick={() => validateComposerSource().catch(() => undefined)}
                disabled={validatingComposer || creatingImport}
              >
                {validatingComposer ? t('imports.composer.validating') : t('imports.composer.validate')}
              </Button>
            )}
            <Button
              className="primary-btn"
              onClick={() => createImportFromComposer().catch(() => undefined)}
              disabled={creatingImport || validatingComposer || !canWriteLinks || (composerUsesAsyncValidation && !csvFile)}
            >
              {creatingImport ? t('imports.composer.creating') : composerSubmissionLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
